import {
  IconAnalytics,
  IconBell,
  IconBinocularsGray,
  IconChainLink,
  IconChartBar,
  IconChartBarGray,
  IconChartRadius,
  IconClockGray,
  IconCloudCheckGray,
  IconDashboard,
  IconLayers,
  IconLocation,
  IconRoute,
  IconRulerGray,
  IconSearch,
  IconSecurity,
  IconShareGray,
  IconTagGray,
  IconTvocGray,
  IconWarning,
  IconZoneGray,
} from '@assets/images/icon';
import { rtlsSystem } from '@assets/images/solution';
import mainVideo from '@src/assets/images/solution/constructionSafety/video/rtlsVideo.mp4';

import image1 from '@src/assets/images/solution/educationSafety/img/1.avif';
import image2 from '@src/assets/images/solution/educationSafety/img/2.avif';
import image3 from '@src/assets/images/solution/educationSafety/img/3.avif';
import image4 from '@src/assets/images/solution/educationSafety/img/4.avif';

import customer1 from '@src/assets/images/solution/publicFire/img/customer1.avif';
import function1 from '@src/assets/images/solution/educationSafety/img/function1.avif';
import function2 from '@src/assets/images/solution/educationSafety/img/function2.avif';
import function3 from '@src/assets/images/solution/educationSafety/img/function3.avif';
import function4 from '@src/assets/images/solution/educationSafety/img/function4.avif';
import { educationSafety1, recommendationEdge, recommendationSwitch } from '@assets/images/solution/recommendation';
import recommendation from '../common/recommendation';

export default {
  type: 'School Solution',

  banner: {
    title: 'Protect school safety smartly with AI video analytics.',
    desc: 'AI uses existing CCTV to detect abnormal behavior and potential hazards on campus, while preserving privacy securely.',
  },

  recommend: {
    title: '',
    desc: 'ORBRO OS is a powerful platform for real‑time location tracking and smart monitoring.<br/>It fully integrates BLE, UWB, and AI-based video analytics to ensure safety, efficiency, and scalability in industrial environments.',
    labels: {
      desktop: [
        { img: educationSafety1, data: recommendation.ipCamera },
        { img: recommendationEdge, data: recommendation.edgeRtls },
        { img: recommendationSwitch, data: recommendation.switch },
      ],
      mobile: [{ data: recommendation.ipCamera }, { data: recommendation.edgeAi }, { data: recommendation.switch }],
    },
  },

  summary: {
    title: 'Detect risks in real time and respond immediately.',
    desc: 'With AI-based pose recognition, the system instantly detects violence, falls, unconsciousness, and sudden behavior among students in hallways, staircases, classrooms, and labs—helping prevent incidents early and respond quickly.',
    function: {
      card: [
        {
          image: function1,
          title: 'Automatic detection of violent behavior (fighting)',
          desc: 'Observes physical conflict like punching or pushing via pose movement and alerts administrators in real time to stop violence early.',
        },
        {
          image: function2,
          title: 'Fall detection and alerts in corridors/stairs',
          desc: 'Recognizes falling actions on stairs or corridors based on pose changes and sends instant alerts for rapid emergency response.',
        },
        {
          image: function3,
          title: 'Detection of lying/unconsciousness on the floor',
          desc: 'Detects prolonged immobility or abnormal posture after a fall, identifying unconsciousness or seizure and sending warnings.',
        },
        {
          image: function4,
          title: 'Detection of abnormal behavior (running, sudden actions)',
          desc: 'Detects sudden running or erratic behavior in corridors or stairs, enabling early action to prevent collisions and accidents.',
        },
      ],
    },
  },

  numOfCard: {
    title: 'Maximize school safety operations with AI technology',
    desc: 'Use real-time detection to respond swiftly and effectively to abnormal behavior and hazards on campus.',
    card: [
      {
        tag: '98%',
        title: 'Abnormal behavior detection accuracy',
        desc: 'AI analysis accurately detects abnormal behavior and hazards, enabling immediate action.',
      },
      {
        tag: '95%',
        title: 'Reduction in personnel requirements',
        desc: 'Automated AI reduces the need for staff, increasing safety management efficiency.',
      },
      {
        tag: '100%',
        title: 'Privacy protection compliance',
        desc: 'No continuous video storage—only short clips are recorded when incidents occur to strictly safeguard privacy.',
      },
    ],
  },

  func: [
    {
      theme: 'Advanced AI features detect and respond to school dangers precisely.',
      title: 'Automatic logging of incident details',
      desc: 'Pose-estimation AI analyzes strong movements, abnormal or aggressive behavior to detect danger automatically and support timely, accurate response.',
      sub1: 'High accuracy',
      icon1: IconRulerGray,
      sub2: 'Real-time status',
      icon2: IconClockGray,
      image: image1,
    },
    {
      title: 'Immediate alerts across 5 risk levels',
      desc: 'The AI categorizes risks into five levels and immediately notifies relevant staff. Responses can be prioritized based on severity.',
      sub2: 'Real-time location tracking',
      icon1: IconBinocularsGray,
      sub1: 'Fast data sync',
      icon2: IconTvocGray,
      image: image2,
    },
    {
      title: 'On-premise, standalone operation supported',
      desc: 'Deploy on your own school server to reduce external dependencies and maintain direct control over data and system updates.',
      sub1: 'Real-time location tracking',
      icon1: IconBinocularsGray,
      sub2: 'High accuracy',
      icon2: IconRulerGray,
      image: image3,
    },
    {
      title: 'Privacy-first, event-triggered video recording',
      desc: 'Only short clips are saved when a risk event is detected, ensuring strong privacy compliance and minimal CCTV burden.',
      sub1: 'Flexible zone settings',
      icon1: IconZoneGray,
      sub2: 'Multiple zone support',
      icon2: IconTagGray,
      image: image4,
    },
  ],

  subfunc: {
    title: 'Feature Summary',
    desc: `Ensuring safety boosts efficiency and reduces losses. ORBRO offers an optimized solution to cope with dangers
      in school environments including hazardous zones and heavy equipment risks.`,
    data: [
      {
        icon: IconAnalytics,
        title: 'AI-based hazard behavior detection',
        desc: 'Automatically analyses movements for abnormal or aggressive behavior to detect danger in real time.',
      },
      {
        icon: IconChartBar,
        title: '5-level risk notifications',
        desc: 'Categorizes danger levels and triggers immediate, clear alerts.',
      },
      {
        icon: IconSearch,
        title: 'Event-triggered video storage',
        desc: 'Only saves short clips at risk events to better protect privacy.',
      },
      {
        icon: IconLayers,
        title: 'Use existing CCTV infrastructure',
        desc: 'Integrates with current cameras for AI analytics without new hardware.',
      },
      {
        icon: IconRoute,
        title: 'Zone-specific deployment',
        desc: 'Apply AI video analytics selectively in prioritized zones like daycare rooms.',
      },
      {
        icon: IconLayers,
        title: 'On-premise independent operation',
        desc: 'School-local servers enable fast, autonomous operation.',
      },
      {
        icon: IconChartBar,
        title: 'Minimal staffing needed',
        desc: 'Automated system maintenance with little or no staff required.',
      },
      {
        icon: IconSearch,
        title: 'Enhanced privacy protection',
        desc: 'Avoids continuous recording, ensures privacy with clip-only saves.',
      },
    ],
  },

  system: {
    title: 'System Configuration',
    img: rtlsSystem,
  },

  productTitle: 'Product Lineup',
  product: [{ tag: 'workerPackage' }, { tag: 'assetPackage' }],

  contents: [{ data: 'constructionAsset' }, { data: 'constructionEquipment' }, { data: 'blogRTLS' }, { data: 'blogMedical' }],
};
